import { useEffect, useMemo, useState } from "react";
import publicApi, { setCustomerToken } from "@/lib/publicApi";
import {
  Bike, CalendarClock, MapPin, Tag, ShieldCheck, Loader2, CheckCircle2,
  XCircle, ChevronRight, ChevronLeft, Info, Clock, Plus, X, Trash2,
  Layers, Ruler, User, LogOut, MailCheck, MailWarning, ClipboardList, Ban, Landmark,
} from "lucide-react";

const eur = (v) => `€ ${Number(v || 0).toFixed(2)}`;
const isoDay = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const addDaysIso = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return isoDay(d); };
const fmtSlotDate = (iso) => { try { return new Date(`${iso}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" }); } catch { return iso; } };
const getParam = (name) => new URLSearchParams(window.location.search).get(name);
const errMsg = (e) => e?.response?.data?.detail || "Si è verificato un errore. Riprova.";

const PAY_META = {
  PayPal: { label: "PayPal", color: "#0070BA" },
  Shop: { label: "Shop Pay", color: "#5A31F4" },
  Satispay: { label: "Satispay", color: "#EE4036" },
  bonifico: { label: "Bonifico bancario", color: "#2b6cb0" },
  satispay: { label: "Satispay", color: "#EE4036" },
  nexi: { label: "Nexi XPay", color: "#00337a" },
  paypal: { label: "PayPal", color: "#0070BA" },
};

export default function PublicBooking() {
  const [config, setConfig] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | book | result
  const [customer, setCustomer] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [myOpen, setMyOpen] = useState(false);
  const [verifyState, setVerifyState] = useState(null); // {ok, email} | {ok:false}
  const [resetToken, setResetToken] = useState(null);

  // booking flow
  const [step, setStep] = useState(0);
  const tomorrowIso = isoDay(new Date(Date.now() + 86400000));
  const [startDate, setStartDate] = useState(tomorrowIso);
  const [fasce, setFasce] = useState([]);
  const [cat, setCat] = useState("");
  const [taglia, setTaglia] = useState("all");
  const [sedeRitiro, setSedeRitiro] = useState("");
  const [avail, setAvail] = useState([]);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [cart, setCart] = useState([]);
  const [accQty, setAccQty] = useState({});
  const accPayload = Object.entries(accQty).map(([id, qta]) => ({ id, qta }));
  const setQty = (id, q) => setAccQty((s) => { const n = { ...s }; if (q <= 0) delete n[id]; else n[id] = q; return n; });
  const [codice, setCodice] = useState("");
  const [note, setNote] = useState("");
  const [quote, setQuote] = useState(null);

  // guest + checkout
  const [guest, setGuest] = useState({ nome: "", cognome: "", email: "", telefono: "" });
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [paying, setPaying] = useState(null);
  const [payErr, setPayErr] = useState(null);
  const [bookingToken, setBookingToken] = useState(null);
  const [bookingInfo, setBookingInfo] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    publicApi.get("/public/config").then((r) => setConfig(r.data)).catch(() => setConfig({ categorie: [], accessori: [], fasce: [], booking_enabled: false }));
    const verifyToken = getParam("verify");
    if (verifyToken) {
      publicApi.post("/public/auth/verify-email", { token: verifyToken })
        .then((r) => setVerifyState({ ok: true, email: r.data.email }))
        .catch(() => setVerifyState({ ok: false }));
    }
    const rToken = getParam("reset");
    if (rToken) setResetToken(rToken);
    publicApi.get("/public/auth/me").then((r) => setCustomer(r.data)).catch(() => { setCustomerToken(null); });
    setPhase("book");
  }, []);

  const slots = config?.fasce || [];
  const sedi = config?.sedi || [];
  const sedeName = (id) => sedi.find((s) => s.id === id)?.nome || "";
  const defaultSlotId = () => (slots.find((s) => s.tipo_tariffa === "giornata") || slots[0])?.id || "";
  useEffect(() => { if (slots.length && fasce.length === 0) setFasce([defaultSlotId()]); /* eslint-disable-next-line */ }, [config]);
  useEffect(() => { if (sedi.length && !sedeRitiro) setSedeRitiro(customer?.sede_preferita || ""); /* eslint-disable-next-line */ }, [config, customer]);

  const giorniPayload = () => fasce.map((fid, i) => ({ data: addDaysIso(startDate, i), fascia_id: fid }));
  const addDay = () => setFasce((f) => [...f, defaultSlotId()]);
  const removeDay = (i) => setFasce((f) => f.filter((_, idx) => idx !== i));
  const setDayFascia = (i, fid) => setFasce((f) => f.map((x, idx) => (idx === i ? fid : x)));
  const catName = (id) => (config?.categorie || []).find((c) => c.id === id)?.nome || "";
  const dp = config?.deposit_percent || 0;

  // availability when category/period change
  const fetchAvail = async () => {
    if (!cat || !fasce.length) { setAvail([]); return; }
    setLoadingAvail(true);
    try {
      const r = await publicApi.post("/public/cart/availability", { categoria_id: cat, giorni: giorniPayload() });
      setAvail(r.data);
    } catch (e) { setAvail([]); }
    finally { setLoadingAvail(false); }
  };
  useEffect(() => { fetchAvail(); setTaglia("all"); /* eslint-disable-next-line */ }, [cat, startDate, JSON.stringify(fasce)]);

  const taglie = useMemo(() => Array.from(new Set(avail.map((b) => b.taglia || "—"))), [avail]);
  const shownBikes = avail.filter((b) => (taglia === "all" || (b.taglia || "—") === taglia) && !cart.some((c) => c.bike_id === b.id))
    .sort((a, b) => ((a.sede_attuale === sedeRitiro || !sedeRitiro) ? 0 : 1) - ((b.sede_attuale === sedeRitiro || !sedeRitiro) ? 0 : 1));
  const addToCart = (b) => setCart((c) => [...c, { bike_id: b.id, numero: b.numero, modello: b.modello, taglia: b.taglia, categoria_id: cat, categoria_nome: catName(cat), sede_attuale: b.sede_attuale, sede_nome: b.sede_nome }]);
  const removeCart = (id) => setCart((c) => c.filter((x) => x.bike_id !== id));

  // live quote (sum per bike, accessori+sconto on first item — mirrors backend)
  const computeQuote = async () => {
    if (!cart.length || !fasce.length) { setQuote(null); return; }
    const giorni = giorniPayload();
    try {
      let totale = 0, cauzione = 0, dettaglio = [], sconto = null, sconto_errore = null;
      for (let i = 0; i < cart.length; i++) {
        const b = cart[i];
        const r = await publicApi.post("/public/quote", {
          categoria_id: b.categoria_id, giorni,
          accessori: i === 0 ? accPayload : [], codice_sconto: i === 0 ? codice : "",
        });
        totale += r.data.totale || 0; cauzione += r.data.cauzione || 0;
        if (i === 0) { sconto = r.data.sconto; sconto_errore = r.data.sconto_errore; }
        dettaglio.push({ numero: b.numero, importo: r.data.totale });
      }
      totale = Math.round(totale * 100) / 100; cauzione = Math.round(cauzione * 100) / 100;
      const acconto = Math.round((totale * dp / 100 + 1e-9) * 100) / 100;
      setQuote({ totale, cauzione, acconto, saldo_in_sede: Math.round((totale - acconto) * 100) / 100, dettaglio, sconto, sconto_errore });
    } catch (e) { setQuote(null); }
  };
  useEffect(() => { if (step >= 1) computeQuote(); /* eslint-disable-next-line */ }, [step, JSON.stringify(cart), JSON.stringify(accQty), codice]);

  const logout = () => { setCustomerToken(null); setCustomer(null); };

  const resendVerify = async () => {
    try { await publicApi.post("/public/auth/resend-verification", { origin_url: window.location.origin }); setVerifyState({ ok: "sent" }); }
    catch (e) { /* noop */ }
  };

  const canStep1 = () => fasce.length && cart.length;
  const guestValid = () => customer || (guest.nome.trim() && guest.cognome.trim() && guest.email.trim() && privacy);

  const pay = async (metodo) => {
    setPayErr(null);
    if (!cart.length) { setPayErr("Aggiungi almeno una bici."); return; }
    if (!customer && !guestValid()) { setPayErr("Compila i tuoi dati e accetta l'informativa privacy."); return; }
    setPaying(metodo);
    try {
      let token = bookingToken;
      let info = bookingInfo;
      if (!token) {
        const body = {
          giorni: giorniPayload(), bike_ids: cart.map((c) => c.bike_id),
          accessori: accPayload, codice_sconto: codice, note, sede_ritiro: sedeRitiro || "", origin_url: window.location.origin,
        };
        if (!customer) Object.assign(body, { nome: guest.nome, cognome: guest.cognome, email: guest.email, telefono: guest.telefono, consenso_privacy: privacy, consenso_marketing: marketing });
        const bk = await publicApi.post("/public/cart/booking", body);
        token = bk.data.permalink_token; info = bk.data;
        setBookingToken(token); setBookingInfo(info);
      }
      const r = await publicApi.post(`/public/cart/${token}/pay`, { metodo });
      if (r.data.redirect && r.data.redirect_url) { window.location.assign(r.data.redirect_url); return; }
      if (r.data.bonifico) {
        setResult({ bonifico: r.data.bonifico, metodo, totale: info?.totale, acconto: info?.caparra, saldo: info?.saldo_in_sede, cauzione: info?.cauzione, bici: info?.bici || cart.map((c) => c.numero), email: customer?.email || guest.email });
      } else {
        setResult({ metodo: r.data.metodo, totale: info?.totale, acconto: info?.caparra, saldo: info?.saldo_in_sede, cauzione: info?.cauzione, bici: info?.bici || cart.map((c) => c.numero), email: customer?.email || guest.email });
      }
      setPhase("result");
    } catch (e) {
      setPayErr(errMsg(e));
      setBookingToken(null); setBookingInfo(null); // allow retry
    } finally { setPaying(null); }
  };

  const resetAll = () => {
    setStep(0); setCart([]); setAccSel([]); setCodice(""); setNote(""); setQuote(null);
    setBookingToken(null); setBookingInfo(null); setResult(null); setPayErr(null); setPhase("book");
    window.history.replaceState({}, "", "/prenota");
  };

  return (
    <div className="min-h-screen bg-[#FAF6F1] text-stone-900" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-5 py-3 flex items-center gap-3">
          <img src="/bicimone-logo.jpg" alt="Bicimone" className="h-10 w-auto max-w-[120px] rounded-md object-contain" />
          <div>
            <div className="font-display text-2xl leading-none tracking-tight">BICIMONE</div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#f98125] font-semibold">Noleggio e-bike</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {customer ? (
              <div className="flex items-center gap-2">
                <button onClick={() => setMyOpen(true)} data-testid="pub-my-bookings-btn" className="hidden sm:flex items-center gap-1 text-xs text-stone-600 hover:text-[#f98125] border border-stone-200 rounded-full px-3 py-1.5"><ClipboardList className="h-3.5 w-3.5" /> Le mie prenotazioni</button>
                <span className="hidden sm:flex items-center gap-1.5 text-sm text-stone-700" data-testid="pub-account-name"><User className="h-4 w-4 text-[#f98125]" /> Ciao, <b>{customer.nome}</b></span>
                <button onClick={logout} data-testid="pub-account-logout" className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800 border border-stone-200 rounded-full px-3 py-1.5"><LogOut className="h-3.5 w-3.5" /> Esci</button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button onClick={() => setAuthOpen("login")} data-testid="pub-account-login-btn" className="text-sm text-stone-700 hover:text-[#f98125] font-semibold px-3 py-1.5">Accedi</button>
                <button onClick={() => setAuthOpen("register")} data-testid="pub-account-register-btn" className="text-sm bg-stone-900 text-white rounded-full px-4 py-1.5 hover:bg-stone-700 transition-colors">Registrati</button>
              </div>
            )}
          </div>
        </div>
      </header>

      {phase === "loading" && (
        <div className="flex items-center justify-center py-40"><Loader2 className="h-8 w-8 animate-spin text-[#f98125]" /></div>
      )}

      {authOpen && <AuthDialog mode={authOpen} sedi={sedi} onClose={() => setAuthOpen(false)} onAuth={(c) => { setCustomer(c); setAuthOpen(false); }} />}
      {resetToken && <ResetPasswordDialog token={resetToken} onClose={() => { setResetToken(null); window.history.replaceState({}, "", "/prenota"); }} onDone={() => setAuthOpen("login")} />}
      {myOpen && customer && <MyBookingsDialog onClose={() => setMyOpen(false)} />}

      {phase === "book" && config && (
        <main className="max-w-5xl mx-auto px-5 py-8">
          {/* email verification banner */}
          {verifyState?.ok === true && <Banner kind="ok" testid="pub-verify-banner" icon={<MailCheck className="h-4 w-4" />} text={`Email ${verifyState.email} verificata con successo!`} />}
          {verifyState?.ok === false && <Banner kind="warn" testid="pub-verify-banner" icon={<MailWarning className="h-4 w-4" />} text="Link di verifica non valido o già utilizzato." />}
          {verifyState?.ok === "sent" && <Banner kind="ok" testid="pub-verify-banner" icon={<MailCheck className="h-4 w-4" />} text="Ti abbiamo inviato una nuova email di verifica." />}
          {customer && !customer.email_verified && verifyState == null && (
            <Banner kind="warn" testid="pub-verify-banner" icon={<MailWarning className="h-4 w-4" />}
              text="La tua email non è ancora verificata."
              action={<button onClick={resendVerify} data-testid="pub-resend-verify" className="underline font-semibold">Reinvia email di verifica</button>} />
          )}

          {!config.booking_enabled ? (
            <div className="max-w-xl mx-auto py-16 text-center">
              <Info className="h-12 w-12 text-[#f98125] mx-auto mb-4" />
              <h2 className="font-display text-3xl">Prenotazioni momentaneamente chiuse</h2>
              <p className="text-stone-600 mt-2">Riprova più tardi o contattaci direttamente. Grazie!</p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="font-display text-5xl sm:text-6xl tracking-tight leading-none">PRENOTA LE TUE E-BIKE</h1>
                <p className="text-stone-600 mt-2 max-w-xl">Scegli il periodo, aggiungi una o più bici al carrello e blocca tutto con un piccolo acconto online.</p>
              </div>

              {/* stepper */}
              <div className="flex items-center gap-2 mb-6 max-w-md">
                {["Periodo & bici", "Extra & prezzo", "Pagamento"].map((s, i) => (
                  <div key={i} className="flex-1">
                    <div className={`h-1.5 rounded-full ${i <= step ? "bg-[#f98125]" : "bg-stone-200"}`} />
                    <div className={`text-[11px] mt-1 ${i === step ? "text-stone-800 font-semibold" : "text-stone-400"}`}>{s}</div>
                  </div>
                ))}
              </div>

              <div className="grid lg:grid-cols-5 gap-6">
                <section className="lg:col-span-3 bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
                  {/* STEP 0 */}
                  {step === 0 && (
                    <div data-testid="pub-step-bici">
                      <div className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-4 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-[#f98125]" /> Periodo del noleggio</div>
                      <label className="block text-xs text-stone-500 mb-1">Data di ritiro</label>
                      <div className="flex items-center justify-between mb-2">
                        <input type="date" data-testid="pub-start-date" value={startDate} min={isoDay(new Date())} onChange={(e) => setStartDate(e.target.value)} className="rounded-lg border border-stone-300 px-3 py-2.5 focus:border-[#f98125] outline-none" />
                        {slots.length > 0 && <button type="button" onClick={addDay} data-testid="pub-add-day" className="flex items-center gap-1 text-xs font-semibold text-[#f98125] hover:text-[#d96a12]"><Plus className="h-3.5 w-3.5" /> Aggiungi giorno</button>}
                      </div>
                      {slots.length === 0 ? <div className="text-sm text-stone-500">Nessuna fascia disponibile.</div> : (
                        <div className="space-y-2 mb-5">
                          {fasce.map((fid, i) => (
                            <div key={i} data-testid={`pub-day-${i}`} className="flex items-center gap-2">
                              <div className="w-28 shrink-0 text-xs text-stone-500 flex items-center gap-1"><Clock className="h-3 w-3 text-stone-400" /> {fmtSlotDate(addDaysIso(startDate, i))}</div>
                              <select data-testid={`pub-fascia-${i}`} value={fid} onChange={(e) => setDayFascia(i, e.target.value)} className="flex-1 rounded-lg border border-stone-300 px-3 py-2 text-sm bg-white outline-none focus:border-[#f98125]">
                                {slots.map((s) => <option key={s.id} value={s.id}>{`${s.nome} (${s.ora_inizio}–${s.ora_fine})`}</option>)}
                              </select>
                              {fasce.length > 1 && <button type="button" onClick={() => removeDay(i)} data-testid={`pub-remove-day-${i}`} className="p-1.5 text-stone-400 hover:text-red-500"><X className="h-4 w-4" /></button>}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="grid sm:grid-cols-2 gap-3 mb-4">
                        <div>
                          <label className="block text-xs uppercase tracking-wider text-stone-500 mb-1.5 flex items-center gap-1"><Layers className="h-3 w-3" /> Tipologia</label>
                          <select data-testid="pub-categoria" value={cat} onChange={(e) => setCat(e.target.value)} className="w-full rounded-lg border border-stone-300 px-3 py-2.5 bg-white outline-none focus:border-[#f98125]">
                            <option value="">Seleziona categoria</option>
                            {(config.categorie || []).map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs uppercase tracking-wider text-stone-500 mb-1.5 flex items-center gap-1"><Ruler className="h-3 w-3" /> Taglia</label>
                          <select data-testid="pub-taglia" value={taglia} onChange={(e) => setTaglia(e.target.value)} disabled={!cat} className="w-full rounded-lg border border-stone-300 px-3 py-2.5 bg-white outline-none focus:border-[#f98125] disabled:opacity-50">
                            <option value="all">Tutte le taglie</option>
                            {taglie.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                      </div>

                      {sedi.length > 0 && (
                        <div className="mb-4">
                          <label className="block text-xs uppercase tracking-wider text-stone-500 mb-1.5 flex items-center gap-1"><MapPin className="h-3 w-3" /> Sede di ritiro</label>
                          <select data-testid="pub-sede-ritiro" value={sedeRitiro} onChange={(e) => setSedeRitiro(e.target.value)} className="w-full rounded-lg border border-stone-300 px-3 py-2.5 bg-white outline-none focus:border-[#f98125]">
                            <option value="">Seleziona sede di ritiro</option>
                            {sedi.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                          </select>
                        </div>
                      )}

                      {cat && (
                        <div className="mb-4" data-testid="pub-avail-list">
                          <div className="text-xs uppercase tracking-wider text-stone-500 mb-2">Bici disponibili {loadingAvail && <Loader2 className="inline h-3 w-3 animate-spin" />}</div>
                          <div className="grid sm:grid-cols-2 gap-2 max-h-44 overflow-y-auto">
                            {shownBikes.map((b) => {
                              return (
                                <button key={b.id} onClick={() => addToCart(b)} data-testid={`pub-bike-${b.id}`} className="flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-lg border text-sm transition-colors border-stone-200 hover:border-[#f98125]">
                                  <span className="flex items-center justify-between w-full"><span>Bici {b.numero} {b.taglia ? `· ${b.taglia}` : ""}</span><Plus className="h-4 w-4 text-[#f98125]" /></span>
                                </button>
                              );
                            })}
                            {!loadingAvail && shownBikes.length === 0 && <div className="sm:col-span-2 text-sm text-stone-500 py-4 text-center">Nessuna bici disponibile per questo periodo.</div>}
                          </div>
                        </div>
                      )}

                      <div>
                        <div className="text-xs uppercase tracking-wider text-stone-500 mb-2 flex items-center justify-between"><span>Carrello bici</span><span className="text-[#f98125] font-semibold">{cart.length}</span></div>
                        {cart.length === 0 ? <div className="text-sm text-stone-500">Vuoto — aggiungi almeno una bici.</div> : (
                          <div className="space-y-1.5" data-testid="pub-cart">
                            {cart.map((c) => {
                              return (
                                <div key={c.bike_id} data-testid={`pub-cart-${c.bike_id}`} className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
                                  <span className="text-sm">Bici {c.numero} · {c.categoria_nome}{c.taglia ? ` · ${c.taglia}` : ""}</span>
                                  <button onClick={() => removeCart(c.bike_id)} className="text-stone-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* STEP 1 */}
                  {step === 1 && (
                    <div data-testid="pub-step-extra">
                      <div className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-4">Accessori & sconto</div>
                      {(config.accessori || []).length > 0 ? (
                        <div className="grid sm:grid-cols-2 gap-2 mb-5">
                          {config.accessori.map((a) => {
                            const q = accQty[a.id] || 0;
                            return (
                            <div key={a.id} data-testid={`pub-acc-${a.id}`} className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm ${q > 0 ? "border-[#f98125] bg-[#f98125]/5" : "border-stone-200"}`}>
                              <span className="flex-1">{a.nome} <span className="text-stone-400">{eur(a.prezzo)}</span></span>
                              <button type="button" onClick={() => setQty(a.id, q - 1)} disabled={q <= 0} data-testid={`pub-acc-minus-${a.id}`} className="h-7 w-7 rounded-md border border-stone-300 disabled:opacity-40 hover:border-[#f98125]">−</button>
                              <span className="w-6 text-center font-mono" data-testid={`pub-acc-qty-${a.id}`}>{q}</span>
                              <button type="button" onClick={() => setQty(a.id, q + 1)} data-testid={`pub-acc-plus-${a.id}`} className="h-7 w-7 rounded-md border border-stone-300 hover:border-[#f98125]">+</button>
                            </div>);
                          })}
                        </div>
                      ) : <div className="text-sm text-stone-500 mb-5">Nessun accessorio disponibile.</div>}
                      <label className="block text-sm font-semibold text-stone-700 mb-1.5 flex items-center gap-1.5"><Tag className="h-4 w-4 text-[#f98125]" /> Codice sconto</label>
                      <input data-testid="pub-codice" value={codice} onChange={(e) => setCodice(e.target.value)} placeholder="opzionale" className="w-full rounded-lg border border-stone-300 px-3 py-2.5 font-mono uppercase outline-none focus:border-[#f98125] mb-4" />
                      <label className="block text-sm font-semibold text-stone-700 mb-1.5">Note (opzionale)</label>
                      <textarea data-testid="pub-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#f98125]" />
                    </div>
                  )}

                  {/* STEP 2 */}
                  {step === 2 && (
                    <div data-testid="pub-step-pagamento">
                      <div className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-4">I tuoi dati</div>
                      {customer ? (
                        <div className="bg-[#f98125]/5 border border-[#f98125]/30 rounded-xl p-4 mb-5" data-testid="pub-logged-customer">
                          <div className="flex items-center gap-2 font-semibold"><User className="h-4 w-4 text-[#f98125]" /> {customer.nome} {customer.cognome}</div>
                          <div className="text-sm text-stone-500">{customer.email}{customer.email_verified ? "" : " · email non verificata"}</div>
                        </div>
                      ) : (
                        <div className="space-y-3 mb-5">
                          <div className="text-sm text-stone-500">Hai un account? <button onClick={() => setAuthOpen("login")} className="text-[#f98125] font-semibold underline" data-testid="pub-inline-login">Accedi</button> oppure continua come ospite:</div>
                          <div className="grid grid-cols-2 gap-2">
                            <input data-testid="pub-nome" placeholder="Nome*" value={guest.nome} onChange={(e) => setGuest({ ...guest, nome: e.target.value })} className="rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#f98125]" />
                            <input data-testid="pub-cognome" placeholder="Cognome*" value={guest.cognome} onChange={(e) => setGuest({ ...guest, cognome: e.target.value })} className="rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#f98125]" />
                          </div>
                          <input data-testid="pub-email" type="email" placeholder="Email*" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#f98125]" />
                          <input data-testid="pub-telefono" placeholder="Telefono" value={guest.telefono} onChange={(e) => setGuest({ ...guest, telefono: e.target.value })} className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#f98125]" />
                          <label className="flex items-start gap-2 text-xs text-stone-600 cursor-pointer">
                            <input type="checkbox" data-testid="pub-privacy" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-0.5 accent-[#f98125]" />
                            <span>Accetto l'informativa sul trattamento dei dati personali (obbligatorio).*</span>
                          </label>
                          <label className="flex items-start gap-2 text-xs text-stone-600 cursor-pointer">
                            <input type="checkbox" data-testid="pub-marketing" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-0.5 accent-[#f98125]" />
                            <span>Desidero ricevere offerte e novità (facoltativo).</span>
                          </label>
                        </div>
                      )}

                      <div className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-3 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#f98125]" /> Paga l'acconto {quote ? eur(quote.acconto) : ""}</div>
                      <div className="space-y-2.5">
                        {(config.metodi_pagamento || []).length === 0 && (
                          <div className="text-sm text-stone-500 bg-stone-50 border border-stone-200 rounded-xl p-4" data-testid="pub-no-methods">Nessun metodo di pagamento online è al momento disponibile. Contatta lo staff per completare la prenotazione.</div>
                        )}
                        {(config.metodi_pagamento || []).map((m) => {
                          const key = typeof m === "string" ? m : m.key;
                          const meta = PAY_META[key] || { label: (typeof m === "object" ? m.nome : m) || key, color: "#f98125" };
                          return (
                            <button key={key} onClick={() => pay(key)} disabled={!!paying} data-testid={`pub-pay-${key.toLowerCase()}`}
                              className="w-full flex items-center justify-center gap-2 rounded-xl text-white font-bold uppercase tracking-wide px-5 py-3.5 transition-opacity disabled:opacity-60"
                              style={{ background: meta.color }}>
                              {paying === key ? <Loader2 className="h-5 w-5 animate-spin" /> : null} {key === "bonifico" ? "Paga con bonifico" : `Paga con ${meta.label}`}
                            </button>
                          );
                        })}
                      </div>
                      {payErr && <div className="text-red-600 text-sm mt-3" data-testid="pub-pay-err">{payErr}</div>}
                    </div>
                  )}

                  {/* nav */}
                  <div className="flex items-center justify-between gap-2 pt-5 mt-5 border-t border-stone-200">
                    <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="pub-back" className="flex items-center gap-1 rounded-xl border border-stone-300 px-4 py-2.5 text-sm hover:bg-stone-50 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /> Indietro</button>
                    {step < 2 && (
                      <button onClick={() => { if (step === 0 && !canStep1()) { return; } setStep((s) => s + 1); }} disabled={step === 0 && !canStep1()} data-testid="pub-next" className="flex items-center gap-1 rounded-xl bg-[#f98125] text-white font-bold px-5 py-2.5 text-sm hover:bg-[#d96a12] disabled:opacity-50">Avanti <ChevronRight className="h-4 w-4" /></button>
                    )}
                  </div>
                </section>

                {/* Summary */}
                <section className="lg:col-span-2">
                  <div className="bg-stone-900 text-white rounded-2xl p-6 shadow-lg sticky top-24">
                    <div className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-4">Riepilogo</div>
                    {cart.length === 0 ? <div className="text-stone-400 text-sm py-8 text-center">Aggiungi delle bici per vedere il preventivo.</div> : (
                      <div className="space-y-2 text-sm" data-testid="pub-summary">
                        <Row k="Periodo" v={`${fmtSlotDate(startDate)} · ${fasce.length} g`} muted />
                        {cart.map((c) => <Row key={c.bike_id} k={`Bici ${c.numero}`} v={c.categoria_nome} muted />)}
                        {quote && (
                          <>
                            <div className="border-t border-white/10 my-2" />
                            {quote.dettaglio.map((d, i) => <Row key={i} k={`Noleggio bici ${d.numero}`} v={eur(d.importo)} />)}
                            {quote.sconto && <Row k={`Sconto ${quote.sconto.codice || ""}`} v={`− ${eur(quote.sconto.importo)}`} accent />}
                            {quote.sconto_errore && <div className="text-amber-400 text-xs" data-testid="pub-sconto-err">⚠ {quote.sconto_errore}</div>}
                            <div className="flex justify-between items-center pt-2 border-t border-white/10">
                              <span className="font-display text-xl">TOTALE</span>
                              <span className="font-display text-3xl text-[#f98125]" data-testid="pub-totale">{eur(quote.totale)}</span>
                            </div>
                            <div className="bg-white/5 rounded-lg p-3 mt-3 space-y-1.5">
                              <div className="flex justify-between text-[#f98125] font-semibold"><span>Acconto online ({dp}%)</span><span data-testid="pub-acconto">{eur(quote.acconto)}</span></div>
                              <div className="flex justify-between text-stone-300 text-xs"><span>Saldo in sede</span><span>{eur(quote.saldo_in_sede)}</span></div>
                              <div className="flex justify-between text-stone-300 text-xs"><span>Cauzione (in sede)</span><span>{eur(quote.cauzione)}</span></div>
                            </div>
                          </>
                        )}
                        <button onClick={() => setShowPolicy((s) => !s)} className="text-xs text-stone-400 underline flex items-center gap-1 mt-2" data-testid="pub-policy-toggle"><ShieldCheck className="h-3.5 w-3.5" /> Policy di cancellazione</button>
                        {showPolicy && <div className="text-[11px] text-stone-400 bg-white/5 rounded-lg p-3" data-testid="pub-policy-text">{config.cancellation_policy}</div>}
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </>
          )}
        </main>
      )}

      {phase === "result" && result && (
        <div className="max-w-xl mx-auto px-5 py-16">
          {result.bonifico ? (
            <div className="bg-white border border-stone-200 rounded-2xl p-10 shadow-sm" data-testid="pub-result-bonifico">
              <div className="text-center">
                <Landmark className="h-14 w-14 text-[#2b6cb0] mx-auto mb-4" />
                <h2 className="font-display text-3xl tracking-tight">Prenotazione registrata</h2>
                <p className="text-stone-600 mt-2">Completa il <b>bonifico</b> dell'acconto di <b>{eur(result.bonifico.importo)}</b> con i dati qui sotto. La prenotazione sarà <b>confermata alla ricezione</b>.</p>
              </div>
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 mt-5 text-left text-sm space-y-2">
                <div className="flex justify-between gap-3"><span className="text-stone-500">Intestatario</span><span className="font-medium text-right">{result.bonifico.intestatario || "—"}</span></div>
                <div className="flex justify-between gap-3"><span className="text-stone-500">IBAN</span><span className="font-mono text-right break-all" data-testid="pub-bonifico-iban">{result.bonifico.iban}</span></div>
                <div className="flex justify-between gap-3"><span className="text-stone-500">Causale</span><span className="text-right" data-testid="pub-bonifico-causale">{result.bonifico.causale}</span></div>
                <div className="flex justify-between gap-3"><span className="text-stone-500">Importo</span><span className="font-medium">{eur(result.bonifico.importo)}</span></div>
              </div>
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 mt-3 text-left text-sm space-y-1.5">
                <div className="flex justify-between"><span className="text-stone-500">Bici</span><span>{(result.bici || []).join(", ")}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Saldo in sede</span><span>{eur(result.saldo)}</span></div>
                <div className="flex justify-between"><span className="text-stone-500">Cauzione (in sede)</span><span>{eur(result.cauzione)}</span></div>
              </div>
              <button onClick={resetAll} data-testid="pub-new-booking" className="mt-6 w-full rounded-xl bg-[#f98125] text-white font-bold uppercase tracking-wide px-6 py-3 hover:bg-[#d96a12] transition-colors">Nuova prenotazione</button>
            </div>
          ) : (
          <div className="bg-white border border-stone-200 rounded-2xl p-10 shadow-sm text-center" data-testid="pub-result">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
            <h2 className="font-display text-3xl tracking-tight">Prenotazione confermata!</h2>
            <p className="text-stone-600 mt-2">Acconto di <b>{eur(result.acconto)}</b> ricevuto con <b>{PAY_META[result.metodo]?.label || result.metodo}</b>. Riceverai una email di conferma{result.email ? ` a ${result.email}` : ""}.</p>
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 mt-5 text-left text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-stone-500">Bici</span><span>{(result.bici || []).join(", ")}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Saldo in sede</span><span>{eur(result.saldo)}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Cauzione (in sede)</span><span>{eur(result.cauzione)}</span></div>
            </div>
            <button onClick={resetAll} data-testid="pub-new-booking" className="mt-6 rounded-xl bg-[#f98125] text-white font-bold uppercase tracking-wide px-6 py-3 hover:bg-[#d96a12] transition-colors">Nuova prenotazione</button>
          </div>
          )}
        </div>
      )}

      <footer className="max-w-5xl mx-auto px-5 py-10 text-center text-xs text-stone-400">
        Bicimone · Noleggio e-bike · Prenotazione online sicura
      </footer>
    </div>
  );
}

function Row({ k, v, muted, accent }) {
  return (
    <div className={`flex justify-between ${accent ? "text-emerald-400" : muted ? "text-stone-400" : "text-stone-200"}`}>
      <span>{k}</span><span className={accent ? "" : "text-white"}>{v}</span>
    </div>
  );
}

function Banner({ kind, icon, text, action, testid }) {
  const cls = kind === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-800";
  return (
    <div data-testid={testid} className={`flex items-center gap-2 text-sm border rounded-xl px-4 py-3 mb-5 ${cls}`}>
      {icon}<span>{text}</span>{action && <span className="ml-auto">{action}</span>}
    </div>
  );
}

function MyBookingsDialog({ onClose }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [methods, setMethods] = useState([]);

  const load = async () => {
    try { setItems((await publicApi.get("/public/auth/my-bookings")).data); }
    catch (e) { setItems([]); }
  };
  useEffect(() => { load(); publicApi.get("/public/config").then((r) => setMethods(r.data.metodi_pagamento || [])).catch(() => {}); }, []);

  const STATO_LABEL = {
    in_attesa_caparra: { t: "In attesa caparra", c: "bg-amber-100 text-amber-700" },
    confermata: { t: "Confermata", c: "bg-emerald-100 text-emerald-700" },
    richiesta: { t: "In attesa caparra", c: "bg-amber-100 text-amber-700" },
    in_corso: { t: "In corso", c: "bg-blue-100 text-blue-700" },
    concluso: { t: "Conclusa", c: "bg-stone-200 text-stone-600" },
    scaduta: { t: "Scaduta", c: "bg-red-100 text-red-600" },
    annullata: { t: "Annullata", c: "bg-red-100 text-red-600" },
  };

  const pay = async (b, metodo) => {
    setBusy(b.id + metodo);
    try {
      const r = await publicApi.post(`/public/auth/bookings/${b.id}/pay`, { metodo });
      if (r.data.redirect && r.data.redirect_url) { window.location.assign(r.data.redirect_url); return; }
      if (r.data.bonifico) {
        const bo = r.data.bonifico;
        window.alert(`Effettua il bonifico dell'acconto (${eur(bo.importo)}):\n\nIntestatario: ${bo.intestatario || "—"}\nIBAN: ${bo.iban}\nCausale: ${bo.causale}\n\nLa prenotazione sarà confermata alla ricezione.`);
      }
      await load();
    }
    catch (e) { /* noop */ }
    finally { setBusy(null); }
  };
  const cancel = async (b) => {
    if (!window.confirm("Annullare questa prenotazione?")) return;
    setBusy(b.id + "cancel");
    try { await publicApi.post(`/public/auth/bookings/${b.id}/cancel`); await load(); }
    catch (e) { /* noop */ }
    finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pub-my-bookings-dialog" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-2xl flex items-center gap-2"><ClipboardList className="h-5 w-5 text-[#f98125]" /> Le mie prenotazioni</div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X className="h-5 w-5" /></button>
        </div>
        {items === null && <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-[#f98125] mx-auto" /></div>}
        {items?.length === 0 && <div className="py-10 text-center text-stone-500 text-sm">Non hai ancora prenotazioni.</div>}
        <div className="space-y-3">
          {(items || []).map((b) => {
            const s = STATO_LABEL[b.stato] || { t: b.stato, c: "bg-stone-200 text-stone-600" };
            const payable = b.stato === "in_attesa_caparra" || b.stato === "richiesta";
            return (
              <div key={b.id} data-testid={`pub-mb-${b.id}`} className="border border-stone-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold text-sm">Bici {(b.bici || []).join(", ") || "—"}</div>
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${s.c}`}>{s.t}</span>
                </div>
                <div className="text-xs text-stone-500 flex items-center gap-1.5"><Clock className="h-3 w-3" /> {fmtSlotDate((b.inizio || "").slice(0, 10))}</div>
                <div className="flex justify-between text-sm mt-2 text-stone-600"><span>Totale {eur(b.totale)}</span><span>Acconto {eur(b.acconto)}</span></div>
                {payable && (
                  <div className="mt-3 pt-3 border-t border-stone-100">
                    <div className="text-[11px] text-stone-500 mb-1.5">Paga l'acconto di {eur(b.acconto)}:</div>
                    <div className="flex flex-wrap gap-2">
                      {methods.length === 0 && <div className="text-[11px] text-stone-400">Nessun metodo di pagamento disponibile. Contatta lo staff.</div>}
                      {methods.map((m) => {
                        const key = typeof m === "string" ? m : m.key;
                        const meta = PAY_META[key] || { label: (typeof m === "object" ? m.nome : m) || key, color: "#f98125" };
                        return (
                          <button key={key} onClick={() => pay(b, key)} disabled={!!busy} data-testid={`pub-mb-pay-${key.toLowerCase()}-${b.id}`} className="flex-1 min-w-[90px] text-xs font-semibold text-white rounded-lg px-3 py-2 disabled:opacity-60" style={{ background: meta.color }}>
                            {busy === b.id + key ? "…" : meta.label}
                          </button>
                        );
                      })}
                    </div>
                    <button onClick={() => cancel(b)} disabled={!!busy} data-testid={`pub-mb-cancel-${b.id}`} className="mt-2 w-full flex items-center justify-center gap-1 text-xs text-red-500 hover:text-red-600 border border-red-200 rounded-lg px-3 py-1.5"><Ban className="h-3.5 w-3.5" /> Annulla prenotazione</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AuthDialog({ mode, sedi = [], onClose, onAuth }) {
  const [tab, setTab] = useState(mode === "register" ? "register" : "login");
  const [f, setF] = useState({ nome: "", cognome: "", email: "", password: "", telefono: "", sede_preferita: "" });
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);

  const submit = async () => {
    setErr(null);
    setLoading(true);
    try {
      if (tab === "forgot") {
        const r = await publicApi.post("/public/auth/forgot-password", { email: f.email, origin_url: window.location.origin });
        setInfo(r.data?.message || "Se l'indirizzo è registrato riceverai un'email con le istruzioni.");
        setLoading(false);
        return;
      }
      if (tab === "login") {
        const r = await publicApi.post("/public/auth/login", { email: f.email, password: f.password });
        setCustomerToken(r.data.token); onAuth(r.data.customer);
      } else {
        if (!privacy) { setErr("Devi accettare l'informativa privacy."); setLoading(false); return; }
        const r = await publicApi.post("/public/auth/register", {
          nome: f.nome, cognome: f.cognome, email: f.email, password: f.password, telefono: f.telefono,
          sede_preferita: f.sede_preferita || "",
          consenso_privacy: privacy, consenso_marketing: marketing, origin_url: window.location.origin,
        });
        setCustomerToken(r.data.token);
        setInfo("Registrazione completata! Ti abbiamo inviato un'email per verificare l'indirizzo.");
        setTimeout(() => onAuth(r.data.customer), 1200);
      }
    } catch (e) { setErr(errMsg(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="pub-auth-dialog" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-2xl">{tab === "login" ? "Accedi" : tab === "forgot" ? "Recupera password" : "Crea il tuo account"}</div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X className="h-5 w-5" /></button>
        </div>
        {tab !== "forgot" ? (
          <div className="flex gap-1 bg-stone-100 rounded-lg p-1 mb-5">
            <button onClick={() => { setTab("login"); setErr(null); setInfo(null); }} data-testid="pub-auth-tab-login" className={`flex-1 py-2 text-sm rounded-md ${tab === "login" ? "bg-white shadow-sm font-semibold" : "text-stone-500"}`}>Accedi</button>
            <button onClick={() => { setTab("register"); setErr(null); setInfo(null); }} data-testid="pub-auth-tab-register" className={`flex-1 py-2 text-sm rounded-md ${tab === "register" ? "bg-white shadow-sm font-semibold" : "text-stone-500"}`}>Registrati</button>
          </div>
        ) : (
          <p className="text-sm text-stone-500 mb-4">Inserisci l'email del tuo account: se è registrata, ti invieremo un link per reimpostare la password (valido 1 ora).</p>
        )}
        <div className="space-y-3">
          {tab === "register" && (
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="pub-auth-nome" placeholder="Nome*" value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} className="rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#f98125]" />
              <input data-testid="pub-auth-cognome" placeholder="Cognome*" value={f.cognome} onChange={(e) => setF({ ...f, cognome: e.target.value })} className="rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#f98125]" />
            </div>
          )}
          <input data-testid="pub-auth-email" type="email" placeholder="Email*" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#f98125]" />
          {tab !== "forgot" && (
            <input data-testid="pub-auth-password" type="password" placeholder="Password*" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#f98125]" />
          )}
          {tab === "login" && (
            <div className="text-right -mt-1">
              <button type="button" onClick={() => { setTab("forgot"); setErr(null); setInfo(null); }} data-testid="pub-forgot-link" className="text-xs text-[#f98125] hover:underline font-semibold">Password dimenticata?</button>
            </div>
          )}
          {tab === "register" && (
            <>
              <input data-testid="pub-auth-telefono" placeholder="Telefono" value={f.telefono} onChange={(e) => setF({ ...f, telefono: e.target.value })} className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#f98125]" />
              {sedi.length > 0 && (
                <select data-testid="pub-auth-sede" value={f.sede_preferita} onChange={(e) => setF({ ...f, sede_preferita: e.target.value })} className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm bg-white outline-none focus:border-[#f98125]">
                  <option value="">Sede preferita (facoltativa)</option>
                  {sedi.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
              )}
              <label className="flex items-start gap-2 text-xs text-stone-600 cursor-pointer">
                <input type="checkbox" data-testid="pub-auth-privacy" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-0.5 accent-[#f98125]" />
                <span>Accetto l'informativa sul trattamento dei dati personali (obbligatorio).*</span>
              </label>
              <label className="flex items-start gap-2 text-xs text-stone-600 cursor-pointer">
                <input type="checkbox" data-testid="pub-auth-marketing" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-0.5 accent-[#f98125]" />
                <span>Desidero ricevere offerte e novità (facoltativo).</span>
              </label>
            </>
          )}
          {err && <div className="text-red-600 text-sm" data-testid="pub-auth-err">{err}</div>}
          {info && <div className="text-emerald-600 text-sm" data-testid="pub-auth-info">{info}</div>}
          <button onClick={submit} disabled={loading} data-testid="pub-auth-submit" className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#f98125] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#d96a12] disabled:opacity-60">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null} {tab === "login" ? "Accedi" : tab === "forgot" ? "Invia istruzioni" : "Registrati"}
          </button>
          {tab === "forgot" && (
            <button type="button" onClick={() => { setTab("login"); setErr(null); setInfo(null); }} data-testid="pub-forgot-back" className="w-full text-center text-sm text-stone-500 hover:text-stone-800 mt-1">← Torna all'accesso</button>
          )}
        </div>
      </div>
    </div>
  );
}

function ResetPasswordDialog({ token, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setErr(null);
    if (pw.length < 6) { setErr("La password deve avere almeno 6 caratteri."); return; }
    if (pw !== pw2) { setErr("Le due password non coincidono."); return; }
    setLoading(true);
    try {
      await publicApi.post("/public/auth/reset-password", { token, password: pw });
      setDone(true);
    } catch (e) { setErr(errMsg(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="pub-reset-dialog" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-2xl">Reimposta password</div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X className="h-5 w-5" /></button>
        </div>
        {done ? (
          <div className="space-y-4 text-center py-2" data-testid="pub-reset-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <p className="text-sm text-stone-600">Password aggiornata con successo. Ora puoi accedere con la nuova password.</p>
            <button onClick={() => { onClose(); onDone && onDone(); }} data-testid="pub-reset-go-login" className="w-full rounded-xl bg-[#f98125] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#d96a12]">Vai all'accesso</button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-stone-500">Scegli una nuova password per il tuo account (almeno 6 caratteri).</p>
            <input data-testid="pub-reset-pw" type="password" placeholder="Nuova password*" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#f98125]" />
            <input data-testid="pub-reset-pw2" type="password" placeholder="Conferma password*" value={pw2} onChange={(e) => setPw2(e.target.value)} className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-[#f98125]" />
            {err && <div className="text-red-600 text-sm" data-testid="pub-reset-err">{err}</div>}
            <button onClick={submit} disabled={loading} data-testid="pub-reset-submit" className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#f98125] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#d96a12] disabled:opacity-60">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Imposta nuova password
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
